import { useState } from "react";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Section } from "@/components/layout/Page";
import { BlogWidget } from "@/components/BlogWidget";
import { CATEGORIES, getCategory, getProductsByCategory } from "@/lib/mock/products";
import { translateStoreSsr } from "@/lib/utils/store-i18n";
import { localizedBrandTitle, metaTags } from "@/lib/utils/seo";
import { getCurrentLocale } from "@/lib/utils/locale";
import { activeDict } from "@/lib/i18n-dict";
import { breadcrumbSchema, itemListSchema, jsonLdScript } from "@/lib/utils/schema-lists";
import { ProductCard } from "@/components/ProductCard";
import { CategoryMosaic, type MosaicTile } from "@/components/CategoryMosaic";
import {
  ArrowRight,
  BookOpen,
  Package,
  ShieldCheck,
  CheckCircle2,
  ChevronRight,
} from "lucide-react";
import type { Product, ProductCategory } from "@/lib/types";
import { useI18n } from "@/context/I18nContext";
import { QuoteDrawer } from "@/components/layout/QuoteDrawer";
import { publishNotFound } from "@/lib/utils/canonical-override";

export const Route = createFileRoute("/products/$category/")({
  loader: ({ params }) => {
    const category = getCategory(params.category);
    if (!category) {
      publishNotFound(`/products/${encodeURIComponent(params.category)}`);
      throw notFound();
    }
    return { category, products: getProductsByCategory(params.category) };
  },
  head: ({ loaderData }) => {
    // The page body already renders cat.<slug> / cat.<slug>.desc, which are
    // translated in all nine locales; the metadata was still reading the raw
    // English strings off the category record, so every locale shipped an
    // English <title> and description.
    const slug = loaderData?.category?.slug;
    const name = slug
      ? translateStoreSsr(`cat.${slug}`, loaderData?.category?.title ?? "")
      : translateStoreSsr("store.seo.categoryFallback", "Category");
    const desc = slug
      ? translateStoreSsr(`cat.${slug}.desc`, loaderData?.category?.description ?? "")
      : "";
    const dict = activeDict();
    const products = loaderData?.products ?? [];
    return {
      meta: metaTags(
        localizedBrandTitle(getCurrentLocale() === "tr" ? `${name} Ürünleri` : name),
        desc,
      ),
      scripts: [
        jsonLdScript(
          "ld-breadcrumb",
          breadcrumbSchema([
            { name: dict["categoryPage.breadcrumb.home"] ?? "Home", path: "/" },
            { name: dict["categoryPage.breadcrumb.catalog"] ?? "Catalog", path: "/products" },
            { name },
          ]),
        ),
        // The grid these describe is the page's whole purpose; without it the
        // products were visible to a reader and invisible as a list.
        jsonLdScript(
          "ld-itemlist",
          itemListSchema(
            name,
            products.map((product) => ({
              name: dict[`product.${product.slug}.title`] ?? product.title,
              path: `/products/${product.category}/${product.slug}`,
            })),
          ),
        ),
      ],
    };
  },
  component: CategoryPage,
});

function CategoryPage() {
  const { t } = useI18n();
  const [quoteOpen, setQuoteOpen] = useState(false);
  const { category, products } = Route.useLoaderData() as {
    category: ProductCategory;
    products: Product[];
  };
  const others = CATEGORIES.filter((c) => c.slug !== category.slug);
  // Titles come from `product.<slug>.title`, the glossary-bound key, so no
  // product is renamed here. Falls back to the record's own title.
  const mosaicTiles: MosaicTile[] = products.slice(0, 4).map((p) => {
    const key = `product.${p.slug}.title`;
    const label = t(key);
    return {
      kind: "product" as const,
      category: p.category,
      slug: p.slug,
      label: label === key ? p.title : label,
      image: p.thumbnail ?? p.image,
    };
  });

  return (
    <div className="bg-background min-h-screen pb-20">
      {/* High-Impact Category Hero Banner */}
      <section className="bg-navy text-white relative overflow-hidden py-12 lg:py-16 border-b border-border/80">
        <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/95 to-navy/80" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 space-y-6">
          {/* Breadcrumb Navigation */}
          <div className="flex items-center gap-2 text-xs font-semibold text-white/60">
            <Link to="/" className="hover:text-mint transition-colors">
              {t("categoryPage.breadcrumb.home")}
            </Link>
            <ChevronRight className="h-3 w-3" />
            <Link to="/products" className="hover:text-mint transition-colors">
              {t("categoryPage.breadcrumb.catalog")}
            </Link>
            <ChevronRight className="h-3 w-3 text-mint" />
            <span className="text-mint font-bold">{t(`cat.${category.slug}`)}</span>
          </div>

          <div className="grid lg:grid-cols-12 gap-8 items-center">
            {/* Left Copy Column */}
            <div className="lg:col-span-7 space-y-4">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-mint/15 text-mint text-xs font-bold uppercase tracking-widest border border-mint/30">
                <Package className="h-4 w-4" /> {t("categoryPage.badge")}
              </div>

              <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-white leading-tight">
                {t(`cat.${category.slug}`)}
              </h1>

              <p className="text-base sm:text-lg text-white/80 leading-relaxed max-w-2xl">
                {t(`cat.${category.slug}.desc`)}
              </p>

              <div className="flex flex-wrap items-center gap-6 text-xs text-white/80 font-semibold pt-4 border-t border-white/10">
                <span className="flex items-center gap-1.5 text-mint">
                  <ShieldCheck className="h-4 w-4" /> {t("categoryPage.trust.quality")}
                </span>
                <span>•</span>
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4 text-mint" />{" "}
                  {t("categoryPage.trust.directSupply")}
                </span>
              </div>
            </div>

            {/* Right Visual Column. Shows this category's own products rather
                than the other three categories — the "Other Categories" section
                further down the page already does that, so a category mosaic
                here would just duplicate it. */}
            <div className="lg:col-span-5 w-full">
              <CategoryMosaic tiles={mosaicTiles} />
            </div>
          </div>
        </div>
      </section>

      {/* Main Catalog Container */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 pt-12 space-y-16">
        {/*
          Category overview.

          These four pages used to carry exactly one string that appeared
          nowhere else on the site — their own <title>. Everything a visitor
          could read was the hero's one-line description (shared with
          /products), a trust strip shared with the other three categories,
          and product cards repeating copy from the product pages. Measured
          with docs/project/seo-audit/measure-content.mjs: three to five own words each,
          on the direct parent of every product page.

          So this is the section that says what the category actually is, how
          it ships and what it costs to start. Nothing here is a new claim —
          it is drawn from the FAQ knowledge base, the export landing pages
          and the product records.
        */}
        <section className="glass-card rounded-3xl border border-border/80 bg-surface-alt/70 p-6 sm:p-10">
          <div className="grid gap-8 lg:grid-cols-12 lg:gap-12">
            {/* Title column. On desktop it stays put while the copy scrolls,
                so the reader always knows which category the text belongs to. */}
            <div className="space-y-4 lg:col-span-4 lg:sticky lg:top-28 lg:self-start">
              <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3.5 py-1.5 text-xs font-bold uppercase tracking-widest text-primary">
                <BookOpen className="h-4 w-4" /> {t("categoryPage.intro.eyebrow")}
              </div>
              <h2 className="font-display text-2xl font-bold leading-tight text-navy sm:text-3xl">
                {t(`cat.${category.slug}.intro.title`)}
              </h2>
              <button
                type="button"
                onClick={() => setQuoteOpen(true)}
                className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-primary/90"
              >
                {t("nav.quote")} <ArrowRight className="h-4 w-4 rtl:rotate-180" />
              </button>
            </div>

            {/* Copy column: the first paragraph carries the argument, so it
                reads as a lead; the two that follow support it side by side. */}
            <div className="lg:col-span-8">
              <p className="text-base font-medium leading-relaxed text-navy sm:text-lg">
                {t(`cat.${category.slug}.intro.p1`)}
              </p>
              <div className="mt-8 grid gap-8 border-t border-border/60 pt-8 sm:grid-cols-2">
                <p className="border-s-2 border-mint ps-4 text-sm leading-relaxed text-muted-foreground">
                  {t(`cat.${category.slug}.intro.p2`)}
                </p>
                <p className="border-s-2 border-mint ps-4 text-sm leading-relaxed text-muted-foreground">
                  {t(`cat.${category.slug}.intro.p3`)}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Category Product Grid (3 Wider Columns) */}
        <section className="space-y-6">
          <div className="flex items-center justify-between pb-3 border-b border-border/60">
            <h2 className="font-display text-2xl font-bold text-navy">
              {t(`cat.${category.slug}`)} {t("categoryPage.portfolio.suffix")} ({products.length})
            </h2>
            {/* Opens in place, like the cards below and the detail page — a
                bulk enquiry should not cost the buyer the category they were
                looking at. No productSlug: this is a category-level enquiry. */}
            <button
              type="button"
              onClick={() => setQuoteOpen(true)}
              className="text-xs font-bold text-primary hover:underline"
            >
              {t("categoryPage.portfolio.bulkQuote")}
            </button>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {products.map((p) => (
              <ProductCard key={p.slug} product={p} />
            ))}
          </div>
        </section>

        {/* Other Categories Links */}
        <section className="glass-card rounded-3xl p-8 border border-border/80 bg-surface-alt/70 space-y-6">
          <h2 className="font-display text-2xl font-bold text-navy">
            {t("categoryPage.otherCategories.heading")}
          </h2>
          <div className="grid gap-4 md:grid-cols-3">
            {others.map((c) => (
              <Link
                key={c.slug}
                to="/products/$category"
                params={{ category: c.slug }}
                className="glass-card group flex items-center justify-between p-5 rounded-2xl bg-background border border-border/80 transition hover:border-primary hover:shadow-lg"
              >
                <div>
                  <h3 className="font-display text-base font-bold text-navy group-hover:text-primary transition-colors">
                    {t(`cat.${c.slug}`)}
                  </h3>
                  <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                    {t(`cat.${c.slug}.desc`)}
                  </p>
                </div>
                <div className="h-8 w-8 rounded-full bg-surface-alt group-hover:bg-primary group-hover:text-white flex items-center justify-center transition-all shrink-0 ml-3">
                  <ArrowRight className="h-4 w-4" />
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Related Articles */}
        <section>
          <BlogWidget category={category.slug} title={t("categoryPage.blogWidgetTitle")} />
        </section>
      </div>

      <QuoteDrawer open={quoteOpen} onOpenChange={setQuoteOpen} />
    </div>
  );
}
